import * as React from 'react'
import type { UseRefinementListProps } from 'react-instantsearch'
import { useRefinementList, useInstantSearch } from 'react-instantsearch'
import type { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList'
import { useTranslations } from 'next-intl'
import { Divider } from 'components/divider'
import { SingleAccordion } from 'components/single-accordion/single-accordion'
import { Checkbox } from 'primitives/cf-checkbox'

type SidebarFilterProps = Omit<UseRefinementListProps, 'attribute'> & {
  header: string
  attribute: string
  isExpanded?: boolean
  isSingleChoice?: boolean
  children?: (props: {
    items: RefinementListItem[]
    handleRefine: (value: string) => void
  }) => React.ReactNode
  selectAll?: boolean
  hideEndDivider?: boolean
}

export function SidebarFilter(props: SidebarFilterProps) {
  const {
    header,
    isExpanded = true,
    showMore = true,
    limit = 20,
    showMoreLimit = 999,
    isSingleChoice = false,
    children,
    selectAll = false,
    hideEndDivider = false,
    ...useRefinementListProps
  } = props
  const t = useTranslations('pages.search.filter')
  const refinementListApi = useRefinementList({
    showMore,
    showMoreLimit,
    limit,
    sortBy: ['count:desc'],
    ...useRefinementListProps,
  })
  const { setIndexUiState } = useInstantSearch()

  const { items, refine, canToggleShowMore, toggleShowMore, isShowingMore } = refinementListApi

  React.useEffect(() => {
    if (selectAll) {
      const itemsToRefine = items.map(item => item.value)

      setIndexUiState(prevState => {
        return {
          ...prevState,
          refinementList: {
            ...prevState.refinementList,
            [useRefinementListProps.attribute]: itemsToRefine,
          },
        }
      })
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!items.length && !children) {
    return null
  }

  const handleRefine = (value: string) => {
    if (isSingleChoice) {
      items?.forEach(item => {
        if (item.value !== value && item.isRefined) {
          refine(item.value)
        }
      })
    }

    refine(value)
  }

  const filterContent = items.map(
    (item, index) =>
      ((limit && index < limit) || isShowingMore) && (
        <div key={item.label + index} className="wrap flex items-center">
          <Checkbox
            idSuffix={header}
            label={
              <>
                <span className="capitalize">{item.label}</span>
                <span className="ml-[5px] text-xs text-gray-500">({item.count})</span>
              </>
            }
            value={item.value}
            checked={item.isRefined}
            onClick={() => handleRefine(item.value)}
          />
        </div>
      ),
  )

  return (
    <div data-testid={`search-side-bar-section-${useRefinementListProps.attribute}`}>
      <SingleAccordion isInitialExpanded={isExpanded} header={header}>
        <div className="flex flex-col gap-[10px]">
          <div className="mt-5 flex flex-col">
            {children ? children({ items, handleRefine }) : filterContent}
          </div>
          {showMore && limit && canToggleShowMore ? (
            <span
              className="cursor-pointer text-xs text-gray-500"
              data-testid="show-more"
              onClick={() => {
                toggleShowMore()
              }}
            >
              {isShowingMore ? t('show_less') : t('show_more')}
            </span>
          ) : null}
        </div>
      </SingleAccordion>
      {!hideEndDivider ? (
        <Divider className="relative left-[-20px] mt-5 w-[calc(100%+40px)] bg-gray-700" />
      ) : null}
    </div>
  )
}
